import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import businessHero from '../assets/business-hero.png';
import { AuthMethodModal } from '../components/auth/AuthMethodModal';
import { Button } from '../components/common';
import { AppLogo } from '../components/common/AppLogo';
import { GoogleAuthButton } from '../components/common/GoogleAuthButton';
import { PasscodeInput } from '../components/common/PasscodeInput';
import { AppVersionWrapper } from '../components/nav/AppVersionWrapper';
import { useAppDispatch, useAppSelector } from '../hooks/useRedux';
import { selectAuthLoading } from '../store/auth/authSelectors';
import {
  fetchCurrentUser,
  login,
  loginWithCode,
} from '../store/auth/authThunks';
import { SignInMethod } from '../types';
import { canSignUp, isElectron } from '../util';
import { apiError, apiSuccess } from '../util/notify';

const LandingPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passcode, setPasscode] = useState('');

  // Auth method state: 'email' or 'code'
  const [authMethod, setAuthMethod] = useState<SignInMethod>('email');

  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectAuthLoading);
  const navigate = useNavigate();

  const handleSelectMethod = (method: SignInMethod) => {
    if (method) localStorage.setItem('auth_method', method);
    setAuthMethod(method);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!email || !password) {
        return;
      }
      const res = await dispatch(login({ email, password })).unwrap();
      if (res?.user?.id) {
        await dispatch(fetchCurrentUser()).unwrap();
        navigate('/sales');
      }
    } catch {
      apiError('Login failed. Please check your credentials and try again.');
    }
  };

  const handlePasscodeSubmit = useCallback(
    async (codeToSubmit: string) => {
      try {
        const res = await dispatch(
          loginWithCode({ code: codeToSubmit }),
        ).unwrap();
        if (res?.user?.id) {
          // Clear the code immediately on success (not just on failure) —
          // loginWithCode and fetchCurrentUser below both toggle the same
          // shared auth loading flag, so there's a brief moment between the
          // two dispatches where it's momentarily false again. Leaving the
          // code at full length during that gap would let PasscodeInput's
          // auto-submit effect fire a second time and log in twice.
          setPasscode('');
          await dispatch(fetchCurrentUser()).unwrap();
          apiSuccess('Logged in successfully');
          navigate('/sales');
        }
      } catch (err) {
        setPasscode(''); // Clear code on failure
        const errMsg =
          typeof err === 'string'
            ? err
            : 'Passcode login failed. Please try again.';
        apiError(errMsg);
      }
    },
    [dispatch, navigate],
  );

  return (
    <div className="min-h-screen relative inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-pos-dark via-pos-primary to-pos-secondary transition-opacity duration-500">
      <div className="max-w-5xl absolute m-auto w-full bg-white rounded-2xl shadow-xl overflow-hidden flex min-h-[660px] h-[78vh] max-h-[820px] z-49">
        {/* Left Side - Auth Panel */}
        <div className="w-full lg:w-1/2 p-10 flex flex-col justify-center overflow-y-auto">
          <div className="max-w-sm mx-auto w-full">
            {/* Logo area */}
            <div className="flex items-center gap-2 mb-6">
              <AppLogo />
              <AppVersionWrapper
                hideLogo
                hideVersion
                className="text-xl font-bold text-gray-900 font-sans"
              />
            </div>

            <div className="mb-6">
              {/* <h1 className="text-2xl font-bold text-gray-900 mb-1 font-sans">
                {authMethod === 'code' ? 'Quick Sign In' : 'Welcome Back!'}
              </h1> */}
              <p className="text-sm text-gray-500">
                {authMethod === 'code'
                  ? 'Enter your 4-digit passcode to log in'
                  : 'Login to manage your business'}
              </p>
            </div>

            {authMethod === 'code' ? (
              /* Passcode Keypad Screen */
              <div className="space-y-6">
                <PasscodeInput
                  code={passcode}
                  onChange={setPasscode}
                  onSubmit={handlePasscodeSubmit}
                  isLoading={isLoading}
                />

                {isElectron() && (
                  <div className="text-center pt-2">
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() => handleSelectMethod('email')}
                      className="text-xs text-pos-primary hover:text-green-700 font-semibold transition-colors"
                    >
                      Switch to Email & Password Login
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Standard Email & Password Form */
              <>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide"
                    >
                      Email Address
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-pos-primary focus:ring-1 focus:ring-pos-primary outline-none transition-all bg-gray-50 hover:bg-white text-sm"
                        placeholder="youremail@gmail.com"
                        required
                        disabled={isLoading}
                      />
                    </label>
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide"
                    >
                      Password
                      <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-pos-primary focus:ring-1 focus:ring-pos-primary outline-none transition-all bg-gray-50 hover:bg-white text-base"
                        placeholder="••••••••••••"
                        required
                        disabled={isLoading}
                      />
                    </label>
                    <div className="flex justify-end mt-1.5">
                      <button
                        type="button"
                        className="text-xs text-gray-500 hover:text-pos-primary font-medium"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    fullWidth
                    size="lg"
                    isLoading={isLoading}
                  >
                    Sign In
                  </Button>
                </form>

                <div className="mt-4">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-100" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-white text-gray-400">
                        or continue with
                      </span>
                    </div>
                  </div>
                  <GoogleAuthButton />
                </div>

                <div className="mt-6 flex flex-col gap-3 items-center">
                  {isElectron() && (
                    <button
                      type="button"
                      onClick={() => handleSelectMethod('code')}
                      className="text-xs text-pos-primary hover:text-green-700 font-semibold transition-colors"
                    >
                      Switch to 4-Digit Passcode Login
                    </button>
                  )}
                  {canSignUp && (
                    <p className="text-sm text-gray-500">
                      Don&apos;t have an account?{' '}
                      <Link
                        to="/onboarding"
                        className="font-bold text-pos-primary hover:text-blue-700"
                      >
                        Sign up
                      </Link>
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Side - Hero Image */}
        <div className="hidden lg:block w-1/2 relative">
          <img
            src={businessHero}
            alt="SmartPOS Business Owner"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        </div>
      </div>

      <span className="flex justify-center items-center absolute bottom-2 text-center z-30">
        <AppVersionWrapper
          hideLogo
          className="text-center text-sm text-white"
        />
        <span className="mx-1 text-xs text-white">
          | <span className="font-bold">By Prodev Innovations Limited</span>
        </span>
      </span>

      {/* Electron Method Selector Popup */}
      <AuthMethodModal onSetAuthMethod={handleSelectMethod} />
    </div>
  );
};

export default LandingPage;
